import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, MessageCircle, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatBRL, whatsappLink, COMPANY_NAME } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setData(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setState("failed"); return; }
      } catch { /* retry */ }
      attempts += 1;
      if (attempts < 8) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 grid place-items-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-xl p-8 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="h-14 w-14 animate-spin text-emerald-600 mx-auto mb-5" />
            <h1 className="font-heading font-bold text-xl text-slate-900">Confirmando seu pagamento...</h1>
            <p className="text-slate-500 mt-2 text-sm">Aguarde um instante, estamos processando.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="h-16 w-16 text-emerald-600 mx-auto mb-5" />
            <h1 className="font-heading font-bold text-2xl text-slate-900">Pagamento confirmado!</h1>
            <p className="text-slate-500 mt-2">Recebemos seu pagamento{data?.amount ? ` de ${formatBRL(data.amount)}` : ""}. Nossa equipe já iniciou o seu atendimento.</p>
            <div className="mt-6 flex flex-col gap-2.5">
              <a href={whatsappLink(`Olá! Acabei de efetuar o pagamento de um serviço na ${COMPANY_NAME}. Gostaria de acompanhar meu atendimento.`)} target="_blank" rel="noreferrer">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-12 gap-2"><MessageCircle className="h-5 w-5" /> Falar no WhatsApp</Button>
              </a>
              <a href="/"><Button variant="outline" className="w-full rounded-full h-12 gap-2"><Home className="h-4 w-4" /> Voltar ao início</Button></a>
            </div>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle className="h-16 w-16 text-amber-500 mx-auto mb-5" />
            <h1 className="font-heading font-bold text-xl text-slate-900">{state === "timeout" ? "Ainda processando" : "Pagamento não concluído"}</h1>
            <p className="text-slate-500 mt-2 text-sm">{state === "timeout" ? "O pagamento pode levar alguns minutos. Em caso de dúvida, fale conosco." : "Não foi possível confirmar o pagamento. Tente novamente ou fale conosco."}</p>
            <div className="mt-6 flex flex-col gap-2.5">
              <a href={whatsappLink(`Olá! Tive um problema com o pagamento na ${COMPANY_NAME}. Podem me ajudar?`)} target="_blank" rel="noreferrer">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-12 gap-2"><MessageCircle className="h-5 w-5" /> Falar no WhatsApp</Button>
              </a>
              <a href="/"><Button variant="outline" className="w-full rounded-full h-12 gap-2"><Home className="h-4 w-4" /> Voltar ao início</Button></a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
